import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';

/**
 * Refetch every batch progress bar in the project after a tag change. Delayed 1s like
 * Angular's reviewStats: the counts come from the search index, which lags the write.
 */
export function refreshBatchReviewMetrics(qc: QueryClient, projectId: string): void {
  setTimeout(() => qc.invalidateQueries({ queryKey: ['batchReviewMetrics', projectId] }), 1000);
}

interface ReviewStatEntry {
  idealResourcesRemaining: number;
  actualResourcesRemaining: number;
  idealBurnRate: number;
  actualBurnRate: number;
}

export interface ReviewStatistics {
  begin: number;
  end: number;
  lastRecordedDate: number;
  statistics: Record<string, ReviewStatEntry>;
}

export interface BatchReviewMetrics {
  reviewed: number;
  notReviewed: number;
}

/**
 * Reviewed / not-reviewed counts for one batch — GET /project/:id/reviewmetrics/batch/:batchId.
 * Drives the batch review progress bar (mirrors Angular's ReviewMetricsService).
 */
export function useBatchReviewMetrics(projectId: string, batchId: string) {
  return useQuery<BatchReviewMetrics>({
    queryKey: ['batchReviewMetrics', projectId, batchId],
    queryFn: () => api.get<BatchReviewMetrics>(`/project/${projectId}/reviewmetrics/batch/${batchId}`),
    enabled: !!projectId && !!batchId,
  });
}

export function useReviewStatistics(projectId: string, options?: { enabled?: boolean }) {
  return useQuery<ReviewStatistics>({
    queryKey: ['review', projectId],
    queryFn: () => api.get<ReviewStatistics>(`/review/${projectId}`),
    enabled: options?.enabled !== false && !!projectId,
  });
}

export function useSetReviewDateRange(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ begin, end }: { begin: Date; end: Date }) => {
      const fmt = (d: Date) => d.toISOString().slice(0, 19) + '+0000';
      return api.put<void>(`/project/${projectId}/review/begin/${fmt(begin)}/end/${fmt(end)}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review', projectId] });
    },
  });
}
